'use client'

// Curated affiliate product pick card: image + name + the per-pick "why this"
// reason + price + a sponsored Buy link. Extracted from soil-test-results.tsx so
// every calculator's curated picks render identically. Analytics slug/context are
// props (was hardcoded to soil-test) so each tool attributes its own clicks.

import { ArrowRight } from 'lucide-react'
import { UiIcon } from '@/components/ui/ui-icon'
import { trackEvent, contentAnalytics } from '@/lib/analytics'
import type { AffiliatePlacement } from '@/lib/analytics'
import { withAmazonTagForPlacement } from '@/lib/affiliate'
import type { ProductRecommendationLink } from '@/lib/product-picks/types'
import type { PickBadge } from '@/data/article-product-picks'
import { PickBadgePill } from '@/components/blog/pick-badge-pill'
import { useAffiliateImpression } from '@/hooks/use-affiliate-impression'

/** A legacy placement, or a tool route's own `tool-*` surface. */
export type ProductPickPlacement = AffiliatePlacement | `tool-${string}`

export function ProductPickCard({
  product,
  pickIndex,
  variant = 'primary',
  trackingSlug,
  sourceContext,
  placement = 'result-page-picks',
  badge,
  ctaLabel = 'Buy',
  imageSize = 'default',
  showPrice = false,
}: {
  product: ProductRecommendationLink
  pickIndex: number
  variant?: 'primary' | 'alternative' | 'shopping-list'
  /** Analytics article_slug, e.g. 'tool:soil-test-analyzer' / 'tool:herbicide-timing'. */
  trackingSlug: string
  /** Analytics source_context — an action id, 'shopping-list', etc. */
  sourceContext: string
  /** Analytics placement. Tool routes pass their own `tool-<slug>-picks` key; the
   *  'result-page-picks' default belongs to the grass-ID result surface. */
  placement?: ProductPickPlacement
  /** Optional "best pick" badge (reuses the article-picks badge pill). */
  badge?: PickBadge
  /** Link CTA copy. Defaults to "Buy" so existing consumers are unchanged. */
  ctaLabel?: string
  /** Thumbnail size. Defaults preserve existing card sizing. */
  imageSize?: 'default' | 'large'
  /** Show the raw Amazon price pill. Defaults OFF — site policy hides price on
   *  tool/rec picks; only comparison surfaces (compare buy panel) pass showPrice. */
  showPrice?: boolean
}) {
  const isCompact = variant === 'shopping-list'
  const imageClass = isCompact ? 'h-12 w-12' : imageSize === 'large' ? 'h-24 w-24' : 'h-16 w-16'
  const containerBg = variant === 'primary' ? 'bg-white border-green-100' : 'bg-white border-gray-200'

  // Impression + unified click on surface = `placement` (e.g. 'result-page-picks',
  // 'compare-picks'), so this shared card's affiliate_product_shown /
  // affiliate_product_clicked join and per-surface CTR is computable everywhere
  // it's used. Fires ALONGSIDE the legacy affiliate_link_clicked below so
  // historical funnels stay intact. These surfaces had click-only tracking.
  const position = pickIndex + 1
  const href = product.affiliateUrl ? withAmazonTagForPlacement(product.affiliateUrl, placement) : ''
  const impressionRef = useAffiliateImpression(placement, {
    article_slug: trackingSlug,
    product_name: product.name,
    affiliate_link: href,
    asin: product.asin ?? undefined,
    source_context: sourceContext,
    pick_index: pickIndex,
    position,
    placement,
  })

  const handleClick = () => {
    trackEvent('affiliate_link_clicked', {
      article_slug: trackingSlug,
      product_name: product.name,
      affiliate_link: href,
      placement,
      asin: product.asin ?? undefined,
      source_context: sourceContext,
      pick_index: pickIndex,
      position,
    })
    contentAnalytics.affiliateProductClicked(placement, {
      productName: product.name,
      affiliateLink: href,
      asin: product.asin ?? undefined,
      article_slug: trackingSlug,
      source_context: sourceContext,
      pick_index: pickIndex,
      position,
      placement,
    })
  }

  return (
    <div ref={impressionRef} className={`grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-lg border ${containerBg} p-2.5 sm:flex sm:flex-row`}>
      <span className={`inline-flex flex-shrink-0 items-center justify-center rounded-md overflow-hidden bg-gray-50 border border-gray-200 ${imageClass}`}>
        {product.imageUrl ? (
          // Use plain img — next/image would require domain config for amazon CDN and the gain is small for a 64px thumbnail
          <img src={product.imageUrl} alt="" className="h-full w-full object-contain" loading="lazy" />
        ) : (
          <span
            className="block h-7 w-6 rounded-sm"
            style={{ background: 'repeating-linear-gradient(135deg, #dcfce7, #dcfce7 4px, #fff 4px, #fff 8px)', border: '1px solid #dcfce7' }}
          />
        )}
      </span>
      <div className="min-w-0 flex-1">
        {badge && (
          <div className="mb-1">
            <PickBadgePill badge={badge} size="sm" />
          </div>
        )}
        <div className="text-[13px] sm:text-[13.5px] font-semibold text-slate-900 leading-[1.35] line-clamp-2">
          {product.name}
        </div>
        {!isCompact && product.reason && (
          <div className="text-[11.5px] text-gray-500 mt-0.5 leading-[1.4] line-clamp-2">{product.reason}</div>
        )}
        {showPrice && product.price && (
          <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-slate-700">
            <span className="rounded bg-gray-100 px-1.5 py-0.5">{product.price}</span>
          </div>
        )}
      </div>
      {product.affiliateUrl && (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer nofollow sponsored"
          data-affiliate="true"
          data-affiliate-placement={placement}
          data-affiliate-position={position}
          onClick={handleClick}
          className="col-span-2 inline-flex w-full flex-shrink-0 items-center justify-center gap-1 min-h-11 min-w-11 rounded-full bg-action px-3 py-2 text-[12px] font-bold text-white hover:bg-action-hover transition-colors no-underline whitespace-normal sm:col-span-1 sm:w-auto"
        >
          {ctaLabel}
          <UiIcon icon={ArrowRight} size={16} directional />
        </a>
      )}
    </div>
  )
}
